// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { shippingCarrierSchema } from '@/constants/shipping-carrier';
import { itemFormSchema } from '@/orders/schemas/item';
import { returnOrderReasonSchema } from '@/return-order/api/types';
import { sparseLocationSchema } from '@/sparse/api/types';
import { dateSchema } from '@/utilities/date-time-schema';
import { z } from 'zod';

// The maximum number of line items a return order may carry
export const RETURN_ORDER_MAX_ITEMS = 50;

// Extend the shared schema with item-level validation for RO
const roItemSchema = itemFormSchema.superRefine((data, ctx) => {
  if (data.item === null) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Item is required',
      path: ['item'],
    });
  }
  if (data.quantity === null || data.quantity <= 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Quantity must be greater than 0',
      path: ['quantity'],
    });
  }
});

export const returnOrderFormSchema = z
  .object({
    returnType: returnOrderReasonSchema.nullable(),
    returnReason: z.string().nullable(),
    fromLocation: sparseLocationSchema.nullable(),
    toLocation: sparseLocationSchema.nullable(),
    promiseShipDate: dateSchema.nullable(),
    promiseDeliveryDate: dateSchema.nullable(),
    orderNotes: z.string().nullable(),
    carrier: shippingCarrierSchema.nullable(),
    referenceNumber: z.string().nullable(),
    otherCarrier: z.string().nullable(),
    items: z
      .array(roItemSchema)
      .min(1, { message: 'At least one item is required' })
      .max(RETURN_ORDER_MAX_ITEMS),
  })
  .refine((data) => data.returnType !== null, {
    message: 'Reason type is required',
    path: ['returnType'],
  })
  .refine((data) => data.fromLocation !== null, {
    message: 'From location is required',
    path: ['fromLocation'],
  })
  .refine((data) => data.toLocation !== null, {
    message: 'To location is required',
    path: ['toLocation'],
  })
  .superRefine((data, ctx) => {
    // Validate that return reason is provided when type is 'other'
    if (
      data.returnType === 'other' &&
      (!data.returnReason || data.returnReason.trim() === '')
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Return reason is required when type is "Other"',
        path: ['returnReason'],
      });
    }
  });

export type ReturnOrderFormData = z.infer<typeof returnOrderFormSchema>;
